import { countries } from '../../constants/countries';
import adminDivisions from '../../constants/country-admin-divisions';
import i18n from '../../i18n';
import { getIdLabel, parseIdKey } from '../ids';
import { processTemplates } from '../template-processor';
import { camelCaseObjectKeys, toCamelCase } from '../text';

const t = i18n.t.bind(i18n);

const TRANSLATION_KEYS_BY_TYPE = {
  default: ['title', 'description', 'help_text'],
  array: ['title', 'description', 'help_text', 'array_item_titles'], // array_item_titles should be an array from backend
  dynamic_array: ['title', 'description', 'help_text', 'array_item_titles'],
  short_text: ['title', 'description', 'help_text', 'placeholder'],
  long_text: ['title', 'description', 'help_text', 'placeholder'],
  number: ['title', 'description', 'help_text', 'placeholder'],
  website: ['title', 'description', 'help_text', 'placeholder'],
  checkbox: ['title', 'help_text'],
};

// SOURCES for options processing (resolveOptionsFromSource)
// Note: Template processing sources are in template-processor.js
const SOURCES = {
  adminDivisions,
  countries,
};

// objects used in translations which need to be snake case
const SKIP_CAMEL_CASE_KEYS = [
  'template_variables',
  'translation_version_keys',
  'configurations',
  'translations',
  'rhf_prefixes',
  'metadata',
];

const safeT = (key, opts = {}) => {
  const { ns, returnObjects, defaultValue, ...rest } = opts;

  const dv = defaultValue !== undefined ? defaultValue : returnObjects ? {} : '';

  if (!key) return dv;

  return i18n.exists(key, { ns }) ? t(key, { ns, returnObjects, defaultValue: dv, ...rest }) : dv;
};

const getTranslatedFields = (field, keys = ['title', 'description']) => {
  const translationKey = field?.baseCode || field?.code;
  const countryCode = field?.countryCode || null;

  if (!translationKey) {
    // Return empty strings for the requested keys
    return keys.reduce((acc, k) => {
      acc[toCamelCase(k)] = '';
      return acc;
    }, {});
  }

  return keys.reduce((acc, key) => {
    const raw = safeT(`${translationKey}.${key}`, {
      ns: 'candidate_fields',
      returnObjects: true,
      defaultValue: '',
    });

    let translation;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const versionKey = field?.translation_version_keys?.[key];
      const extracted =
        (countryCode && raw[countryCode]) || (versionKey && raw[versionKey]) || raw.default;
      // Preserve arrays - don't convert to string
      if (Array.isArray(extracted)) {
        translation = extracted;
      } else if (extracted !== undefined && extracted !== null) {
        translation = extracted;
      } else {
        translation = '';
      }
    } else if (Array.isArray(raw)) {
      translation = raw;
    } else if (typeof raw === 'string') {
      translation = raw;
    } else {
      translation = '';
    }

    acc[toCamelCase(key)] = translation;
    return acc;
  }, {});
};

export const resolveOptionsFromSource = (
  path,
  { nameKey = 'name', valueKey = 'value', exclude = [] } = {},
) => {
  if (!path || typeof path !== 'string') return [];

  // extract top-level source name
  const [sourceName, ...nestedKeys] = path.split('.');
  const sourceObject = SOURCES?.[sourceName];
  if (!sourceObject) return [];

  // resolve nested keys
  let resolved = sourceObject;
  for (const key of nestedKeys) {
    if (resolved == null) return [];
    resolved = resolved[key];
  }

  // Fallback to top-level if no nested path was found
  resolved = resolved ?? SOURCES?.[path];
  if (!resolved || typeof resolved !== 'object') return [];

  const rawOptions = Array.isArray(resolved) ? resolved : Object.values(resolved);

  return rawOptions
    .filter(opt => !exclude.includes(opt[valueKey]))
    .map(opt => ({
      name: opt[nameKey] ?? opt[valueKey],
      value: opt[valueKey],
    }));
};

const processOptions = (field, ns = 'candidate_fields') => {
  const options = field?.options;
  const baseCode = field?.baseCode || field?.code;

  if (!options) return [];

  // object with `source` key — resolve from constants (supports `exclude`, nameKey, valueKey)
  if (
    typeof options === 'object' &&
    !Array.isArray(options) &&
    typeof options.source === 'string'
  ) {
    const exclude = options.exclude || [];
    const nameKey = options.nameKey || 'name';
    const valueKey = options.valueKey || 'value';

    return resolveOptionsFromSource(options.source, {
      nameKey,
      valueKey,
      exclude,
    });
  }

  // array of options
  if (Array.isArray(options)) {
    return options
      .map(option => {
        // primitive option -> { value, name }
        if (typeof option !== 'object' || option === null) {
          const value = option;
          const key = `${baseCode}.options.${String(value)}`;
          const name = baseCode ? safeT(key, { ns, defaultValue: String(value) }) : String(value);
          return { value, name };
        }

        const opt = { ...option };

        // option with `value` key (even falsy like 0), use translation only if name is missing
        if ('value' in opt) {
          if (opt.name == null) {
            const key = `${baseCode}.options.${String(opt.value)}`;
            opt.name = baseCode
              ? safeT(key, { ns, defaultValue: String(opt.value) })
              : String(opt.value);
          }
          return opt;
        }

        // no `value`, but has `name` -> use name as value too
        if (opt.name != null && !('value' in opt)) {
          return { ...opt, value: opt.name };
        }

        // drop invalid shapes
        return null;
      })
      .filter(Boolean);
  }

  return [];
};

const pickLocalized = (val, countryCode) => {
  // accepts a string OR an object like { default: "...", AU: "..." }
  if (val && typeof val === 'object' && !Array.isArray(val)) {
    return (countryCode && val[countryCode]) ?? val.default ?? '';
  }
  return val;
};

const processIdOptions = (field, candidateTranslationVariables = {}) => {
  const options = Array.isArray(field?.options) ? field.options : [];
  const baseCode = field?.baseCode || field?.code;

  if (options.length === 0) return [];

  return options
    .map(option => {
      if (
        option == null ||
        typeof option !== 'object' ||
        !Object.prototype.hasOwnProperty.call(option, 'value')
      ) {
        return null;
      }

      const value = option.value;
      // Parse idType and countryCode from value if not already present
      let idType = option.idType ?? null;
      let countryCode = option.countryCode ?? null;

      if (!idType && typeof value === 'string') {
        const parsed = parseIdKey(value);
        idType = parsed.idType || null;
        countryCode = parsed.countryCode || countryCode;
      }

      // i18n payloads -> camelCase only top-level keys, preserve nested country codes
      const idTypeDataRaw = safeT(idType, {
        ns: 'id_document_types',
        returnObjects: true,
        defaultValue: {},
      });
      const idTypeData = Object.fromEntries(
        Object.entries(idTypeDataRaw).map(([key, value]) => [
          toCamelCase(key), // camelCase top-level keys like 'name', 'description'
          value, // preserve nested structure (country codes, etc.)
        ]),
      );

      const candidateFieldDataRaw = safeT(`${baseCode}.options.${idType}`, {
        ns: 'candidate_fields',
        returnObjects: true,
        defaultValue: {},
      });
      const candidateFieldData = Object.fromEntries(
        Object.entries(candidateFieldDataRaw).map(([key, value]) => [
          toCamelCase(key), // camelCase top-level keys like 'name', 'description'
          value, // preserve nested structure (country codes, etc.)
        ]),
      );

      // resolve name
      const cfName = candidateFieldData?.name ?? null;
      let name =
        cfName != undefined
          ? pickLocalized(cfName, countryCode)
          : getIdLabel(idType, countryCode, {
              prefix: candidateFieldData?.prefix
                ? pickLocalized(candidateFieldData.prefix, countryCode)
                : undefined,
              suffix: candidateFieldData?.suffix
                ? pickLocalized(candidateFieldData.suffix, countryCode)
                : undefined,
            });

      // resolve description
      const descriptionRaw = idTypeData?.description;
      let description =
        descriptionRaw !== undefined
          ? pickLocalized(descriptionRaw, countryCode) || undefined
          : undefined;

      // resolve additional instructions
      const additionalInstructionsRaw = candidateFieldData?.additionalInstructions;
      let additionalInstructions = undefined;
      if (additionalInstructionsRaw !== undefined) {
        let processed = pickLocalized(additionalInstructionsRaw, countryCode);
        if (processed && typeof processed === 'string' && processed.trim() !== '') {
          additionalInstructions = processed;
        }
      }

      // resolve complementary instructions
      const complementaryInstructionsRaw = candidateFieldData?.complementaryInstructions;
      let complementaryInstructions = undefined;
      if (complementaryInstructionsRaw !== undefined) {
        let processed = pickLocalized(complementaryInstructionsRaw, countryCode);
        if (processed && typeof processed === 'string' && processed.trim() !== '') {
          complementaryInstructions = processed;
        }
      }

      // resolve input fields
      const inputFields = (option.inputFields ?? []).map(field =>
        processField(field, candidateTranslationVariables),
      );

      const result = {
        ...option,
        name,
        description,
        value,
        inputFields,
      };

      // Only include instructions if they exist and are non-empty strings
      if (additionalInstructions) {
        result.additionalInstructions = additionalInstructions;
      }
      if (complementaryInstructions) {
        result.complementaryInstructions = complementaryInstructions;
      }

      return result;
    })
    .filter(Boolean);
};

const processRuntimeConfig = (field, translationKeys = [], candidateTranslationVariables = {}) => {
  const runtimeConfig = field?.runtimeConfig;
  if (!runtimeConfig?.configurations) return undefined;

  const updatedConfigurations = {};

  for (const [configKey, config] of Object.entries(runtimeConfig.configurations)) {
    const translated = {};

    for (const [innerKey, innerValue] of Object.entries(config)) {
      const camelKey = toCamelCase(innerKey);

      if (translationKeys.includes(innerKey)) {
        // create synthetic field with code + version key
        const syntheticField = {
          code: field?.code,
          translation_version_keys: {
            [innerKey]: innerValue,
          },
        };

        translated[camelKey] = getTranslatedFields(syntheticField, [innerKey])[innerKey];
      } else {
        // CamelCase the first level of keys in the value for all non-translation keys
        const camelCasedValue = camelCaseObjectKeys(innerValue);

        if (innerKey === 'options') {
          const fieldType = field?.type;

          if (fieldType === 'id_requirements_list') {
            const options = Array.isArray(camelCasedValue) ? camelCasedValue : [];
            // CamelCase all keys in options (including input_fields)
            const camelCasedOptions = options.map(option => camelCaseObjectKeys(option));

            translated[camelKey] = processIdOptions(
              { ...field, options: camelCasedOptions },
              candidateTranslationVariables,
            );
          } else {
            translated[camelKey] = processOptions(
              { ...field, options: camelCasedValue },
              'candidate_fields',
            );
          }
        } else {
          translated[camelKey] = camelCasedValue;
        }
      }
    }

    updatedConfigurations[configKey] = translated;
  }

  return {
    ...runtimeConfig,
    configurations: updatedConfigurations,
  };
};

const processField = (field, candidateTranslationVariables = {}) => {
  // translations
  const translationKeys = TRANSLATION_KEYS_BY_TYPE[field?.type] || TRANSLATION_KEYS_BY_TYPE.default;

  const translatedFields = getTranslatedFields(field, translationKeys);

  if (translationKeys.includes('placeholder') && !translatedFields.placeholder) {
    translatedFields.placeholder = translatedFields.title || '';
  }

  // type specific placeholder overrides
  if (field?.type === 'website') {
    let placeholder = safeT(`${field?.baseCode || field?.code}.placeholder`, {
      ns: 'candidate_fields',
      defaultValue: '',
    });

    if (!placeholder) {
      placeholder = safeT(`common.websitePlaceholder`);
    }
    translatedFields.placeholder = placeholder;
  }
  if (field?.type === 'select' || field?.type === 'multi_select') {
    translatedFields.placeholder = safeT(`common.selectPlaceholder`);
  }
  if (
    field?.type === 'country' ||
    field?.type === 'country_alpha2' ||
    field?.type === 'country_alpha3'
  ) {
    translatedFields.placeholder = safeT(`common.selectCountryPlaceholder`);
  }
  if (
    field?.type === 'multi_country' ||
    field?.type === 'multi_country_alpha2' ||
    field?.type === 'multi_country_alpha3'
  ) {
    translatedFields.placeholder = safeT(`common.selectPlaceholder`);
  }
  if (field?.type === 'email') {
    translatedFields.placeholder = safeT(`common.emailPlaceholder`);
  }
  if (field?.type === 'phone_number') {
    translatedFields.placeholder = safeT(`common.phoneNumber`);
  }

  Object.assign(field, translatedFields);

  // options processing
  if (
    field?.type === 'multiple_choice' ||
    field?.type === 'select' ||
    field?.type === 'multi_select'
  ) {
    const processedOptions = processOptions(field, 'candidate_fields');
    if (field?.type === 'multiple_choice') {
      // wrap in options object
      field.options = {
        options: processedOptions,
        multipleSelection: false,
        otherOption: false,
      };
    } else {
      // keep as flat array
      field.options = processedOptions;
    }
  }

  // countries - populate options & normalize to select/multi_select
  if (
    [
      'country',
      'country_alpha2',
      'country_alpha3',
      'multi_country',
      'multi_country_alpha2',
      'multi_country_alpha3',
    ].includes(field?.type)
  ) {
    field.options = resolveOptionsFromSource('countries', {
      nameKey: 'name',
      valueKey:
        field.type === 'country_alpha2' || field.type === 'multi_country_alpha2'
          ? 'alpha2'
          : field.type === 'country_alpha3' || field.type === 'multi_country_alpha3'
            ? 'alpha3'
            : 'value',
      exclude: field?.options?.exclude || [],
    });
    field.type = field?.type?.includes('multi') ? 'multi_select' : 'select';
  }

  // id requirements - alternative id instructions
  if (field?.type === 'id_requirements') {
    if (field?.inclAltIdInstructions === undefined && field?.hasIdRequirements) {
      field.inclAltIdInstructions = true;
    }

    if (field?.inclAltIdInstructions) {
      ['alt_id_instructions_checkbox_title', 'alt_id_instructions'].forEach(key => {
        let value = safeT(`${field?.code}.${key}`, {
          ns: 'candidate_fields',
          defaultValue: '',
        });

        if (!value) {
          value = safeT(`${key}`, {
            ns: 'candidate_fields',
            defaultValue: '',
          });
        }
        field[toCamelCase(key)] = value;
      });
    }
  }

  // id options processing
  if (field?.type === 'id_requirements_list') {
    field.options = processIdOptions(field, candidateTranslationVariables);
  }

  // runtime config processing
  if (field?.runtimeConfig) {
    const runtimeConfig = field?.runtimeConfig?.configurations
      ? processRuntimeConfig(field, translationKeys, candidateTranslationVariables)
      : field?.runtimeConfig;

    field.runtimeConfig = runtimeConfig;
  }

  // child fields processing
  if (Array.isArray(field?.subFields) && field?.subFields?.length > 0) {
    field.subFields = field?.subFields?.map(subField =>
      processField(subField, candidateTranslationVariables),
    );
  }

  if (Array.isArray(field?.altIdFields) && field?.altIdFields?.length > 0) {
    field.altIdFields = field?.altIdFields?.map(altIdField =>
      processField(altIdField, candidateTranslationVariables),
    );
  }

  // constant templates processing
  // Spread candidateTranslationVariables first to provide defaults (like job_role),
  // then template_variables can override if needed
  const templateSources = {
    ...candidateTranslationVariables,
    ...(field?.template_variables || {}),
  };

  const processedField = processTemplates(
    field,
    templateSources,
    true, // preserveOnNull = true for static processing
  );

  Object.assign(field, processedField);

  // Process runtime config templates if they exist
  if (field?.runtimeConfig?.translations) {
    const processedRuntimeConfig = { ...field.runtimeConfig };

    // Replace template keys with resolved field values
    Object.keys(processedRuntimeConfig.translations).forEach(fieldKey => {
      const fieldValue = field[fieldKey];

      if (fieldValue && typeof fieldValue === 'string' && fieldValue.includes('{{')) {
        // Replace the translation key with the resolved field value (with remaining unresolved parts)
        processedRuntimeConfig.translations[fieldKey] = fieldValue;
      }
    });

    field.runtimeConfig = processedRuntimeConfig;
  }

  // delete template variables & translation version keys
  delete field.template_variables;
  delete field.translation_version_keys;

  return field;
};

export const processStaticContent = (sections = [], candidateTranslationVariables = {}) => {
  const s = camelCaseObjectKeys(sections, SKIP_CAMEL_CASE_KEYS);
  return s.map(section => {
    const translatedSection = getTranslatedFields(section, ['title', 'description']);

    return {
      ...section,
      ...translatedSection,
      subsections: (section.subsections || []).map(subsection => {
        const processedFields = (subsection.fields || [])
          .map(field => processField(field, candidateTranslationVariables))
          .filter(Boolean);

        return {
          ...subsection,
          fields: processedFields,
        };
      }),
    };
  });
};
